import { useState, useCallback, useRef, useEffect, useMemo, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AgGridReact } from 'ag-grid-react';

import {
    FundNowLogo,
    ExpandableFeatureLayout,
    DropzoneFile,
    Dropzone,
    DropOverlay,
} from '@lendica/components';
import { InvoicesTable } from '../components/InvoicesTable';
import ArchiveDialog from '../components/ArchiveDialog';
import { MultiUploadDialog } from '../components/MultiUploadDialog';
import { InvoicePdfViewerResponsive } from './InvoicePdfViewerResponsive';
import { UploadInvoice } from './UploadInvoice';
import { useInvoiceProcessing } from '../hooks/useInvoiceUploadProgress';
import { useContainerWidth } from '@lendica/hooks';
import { CreditStatsRow } from '../components/CreditStatsRow';

import {
    InvoiceStatus,
    InvoiceTableRowStatus,
    DealStatus,
    DealDisplayStatus,
    invoiceOrDealStatusToLabel,
    createInvoiceObjectKey,
} from '@lendica/utils';
import { RowType, FundNowDataProps, NULL_VALUE_FALLBACK } from '../sharedTypes';
import { useFundnowApi, presignedUrl, bucket_name } from '../api';
import { useCompanyNavigate } from '@lendica/hooks';

export const FundNowViewResponsive = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<FundNowDataProps | null>(null);
    const [invoiceId, setInvoiceId] = useState('');
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [multiUploadDialogOpen, setMultiUploadDialogOpen] = useState(false);
    const [isDropZoneActive, setIsDropZoneActive] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<DropzoneFile[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [errorOpen, setErrorOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [invoicesTotal, setInvoicesTotal] = useState(0);
    const [dealsTotal, setDealsTotal] = useState(0);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const navigate = useCompanyNavigate();
    const api = useFundnowApi();
    const { startProcessing, clearCompleted } = useInvoiceProcessing();
    const containerBreakpoints = useContainerWidth();

    const refreshInvoicesList = async () => {
        setLoading(true);
        const invoicesResp = await api.getInvoicesList(page, pageSize);
        setData(prevState => ({
            approval: prevState?.approval,
            deals: prevState?.deals,
            invoices: invoicesResp.results,
        }));
        setInvoicesTotal(invoicesResp.count || invoicesResp.results.length || 0);
        setLoading(false);
    };

    const handleArchiveDialogClose = async (archived: boolean) => {
        if (archived) {
            await refreshInvoicesList();
        }
        setInvoiceId('');
        setArchiveDialogOpen(false);
    };

    // Handlers for drag and drop functionality
    const handleFileDrop = (files: DropzoneFile[]) => {
        console.log('Files dropped in PayLater:', files);
        // setIsDropZoneActive(false); // Hide overlay after drop
    };

    const handleFileUpload = (files: DropzoneFile[]) => {
        setUploadedFiles(files);
        // For multiple files, show the dialog
        // For single file, the UploadInvoice component will handle it
        if (files.length > 1) {
            files.forEach(file => {
                if (file.key) {
                    startProcessing(file);
                }
            });
            setMultiUploadDialogOpen(true);
        }
    };

    const handleMultiUploadDialogClose = () => {
        setMultiUploadDialogOpen(false);
        setUploadedFiles([]); // Clear files when closing
        clearCompleted();
        setIsDropZoneActive(false);
        // refresh the invoice table
        refreshInvoicesList();
    };

    const handleUploadFailed = (error: Error) => {
        console.error('Upload failed in PayLater:', error);
        setErrorMessage(error.message);
        setErrorOpen(true);
        setIsDropZoneActive(false); // Hide overlay on error
    };

    const handleErrorClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setErrorOpen(false);
    };

    const createObjectKey = (fileName: string) => {
        return createInvoiceObjectKey(fileName, data!.approval!.company);
    };

    const handleAddInvoicesClick = useCallback(() => {
        setIsDropZoneActive(!isDropZoneActive);
    }, [isDropZoneActive]);

    const handleCloseOverlay = useCallback(() => {
        clearCompleted();
        setIsDropZoneActive(false);
    }, []);

    const handleClickAway = useCallback(
        (event: Event) => {
            // Check if the click was on the button - if so, ignore it
            if (buttonRef.current && buttonRef.current.contains(event.target as Node)) {
                return;
            }

            // Don't close dropzone when multiupload dialog is open
            if (multiUploadDialogOpen) {
                return;
            }

            if (isDropZoneActive) {
                setIsDropZoneActive(false);
            }
        },
        [isDropZoneActive, multiUploadDialogOpen]
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const approvalResp = await api.getFundnowApproval();
                const [invoicesResp, dealsResp] = await Promise.all([
                    api.getInvoicesList(1, pageSize),
                    api.getDealsList(1, pageSize),
                ]);
                if (cancelled) return;

                setData({
                    approval: approvalResp.results?.[0],
                    invoices: invoicesResp.results,
                    deals: dealsResp.results,
                });
                setPage(1);
                setPageSize(pageSize);
                setInvoicesTotal(invoicesResp.count || invoicesResp.results.length || 0);
                setDealsTotal(dealsResp.count || dealsResp.results.length || 0);

                const company = await api.getCompanyDetail();
                if (cancelled) return;
                if (company.status === 2) {
                    if (company.fundnow_status === 0) {
                        navigate('/fundnow/waitlist');
                    } else if (company.fundnow_status === 1) {
                        navigate('/fundnow/product-pending');
                    } else if (company.fundnow_status === 3) {
                        navigate('/fundnow/product-paused');
                    }
                }
            } catch (error) {
                console.error('Error fetching fundnow data:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [api]);

    const loadPage = useCallback(
        async (nextPage: number, nextPageSize: number) => {
            setLoading(true);
            try {
                const [invoicesResp, dealsResp] = await Promise.all([
                    api.getInvoicesList(nextPage, nextPageSize),
                    api.getDealsList(nextPage, nextPageSize),
                ]);
                setData(prev => ({
                    approval: prev?.approval,
                    invoices: invoicesResp.results,
                    deals: dealsResp.results,
                }));
                setPage(nextPage);
                setPageSize(nextPageSize);
                setInvoicesTotal(invoicesResp.count || invoicesResp.results.length || 0);
                setDealsTotal(dealsResp.count || dealsResp.results.length || 0);
            } finally {
                setLoading(false);
            }
        },
        [api]
    );

    const mainContent = (
        <ExpandableFeatureLayout maxWidth={1280} loading={loading}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: containerBreakpoints.isWide ? 4 : 2,
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    height: '100%',
                    width: '100%',
                    p: containerBreakpoints.isNarrow ? 2 : 4,
                }}
            >
                {/* Header row - outside ClickAwayListener */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        gap: containerBreakpoints.isWide ? 0 : 1,
                        zIndex: 1000, // Above overlay
                        position: 'relative',
                    }}
                >
                    <FundNowLogo />
                    <Button
                        ref={buttonRef}
                        variant="contained"
                        color="primary"
                        size={containerBreakpoints.isWide ? 'large' : 'medium'}
                        endIcon={<KeyboardArrowDownIcon id="arrow-down-icon" />}
                        onClick={handleAddInvoicesClick}
                        sx={{
                            '& #arrow-down-icon': {
                                transition: 'all 0.2s ease-in-out',
                                transform: isDropZoneActive ? 'rotate(180deg)' : 'rotate(0deg)',
                            },
                        }}
                    >
                        {isDropZoneActive ? 'Close' : 'Add Invoices'}
                    </Button>
                </Box>

                {/* Content area wrapped with ClickAwayListener and DropOverlay */}
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Box sx={{ width: '100%', height: '100%', flex: 1 }} role="presentation">
                        <DropOverlay
                            isActive={isDropZoneActive}
                            presignedUrl={presignedUrl}
                            bucket_name={bucket_name}
                            onFileDrop={handleFileDrop}
                            onFileUpload={handleFileUpload}
                            onUploadFailed={handleUploadFailed}
                            getObjectKey={createObjectKey}
                            maxFiles={20} // Allow maximum 5 files
                            maxSize={10 * 1024 * 1024} // 10MB per file
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                    width: '100%',
                                    height: '100%',
                                    flex: 1,
                                }}
                                onClick={isDropZoneActive ? handleCloseOverlay : undefined}
                            >
                                <Suspense
                                    fallback={<SuspenseFallback />}
                                    key={loading && !data ? 'loading' : 'loaded'}
                                >
                                    {/* Stats cards */}
                                    {data && (
                                        <CreditStatsRow
                                            data={data}
                                            containerBreakpoints={containerBreakpoints}
                                        />
                                    )}
                                </Suspense>

                                {/* Table container */}
                                {data && (
                                    <MappedInvoicesTable
                                        data={data}
                                        page={page}
                                        pageSize={pageSize}
                                        invoicesTotal={invoicesTotal}
                                        dealsTotal={dealsTotal}
                                        loadPage={loadPage}
                                        onArchiveInvoice={invoiceId => {
                                            setInvoiceId(invoiceId);
                                            setArchiveDialogOpen(true);
                                        }}
                                    />
                                )}
                            </Box>
                        </DropOverlay>
                    </Box>
                </ClickAwayListener>
                <ArchiveDialog
                    open={archiveDialogOpen}
                    invoiceId={invoiceId}
                    onClose={handleArchiveDialogClose}
                />
                <MultiUploadDialog
                    open={multiUploadDialogOpen}
                    // open={true}
                    // files={testFiles}
                    onClose={handleMultiUploadDialogClose}
                    files={uploadedFiles}
                />
            </Box>
        </ExpandableFeatureLayout>
    );

    return (
        <>
            {uploadedFiles.length === 1 ? (
                <>
                    <Box sx={{ display: 'none' }}>
                        <Dropzone
                            value={uploadedFiles}
                            onChange={handleFileUpload}
                            maxFiles={1} // Single file only
                            maxSize={10 * 1024 * 1024} // 10MB per file
                            api={{
                                presignedUrl: presignedUrl,
                                getObjectKey: createObjectKey,
                                presignedUrlParams: {
                                    bucket_name: bucket_name,
                                },
                            }}
                        />
                    </Box>
                    <UploadInvoice
                        files={uploadedFiles}
                        onExit={() => {
                            setUploadedFiles([]);
                        }}
                    />
                </>
            ) : (
                <Box ref={containerBreakpoints.ref}>
                    {mainContent}
                    <Routes>
                        <Route path=":type/:id/view" element={<InvoicePdfViewerResponsive />} />
                    </Routes>
                </Box>
            )}
            <Snackbar
                open={errorOpen}
                autoHideDuration={6000}
                onClose={handleErrorClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={handleErrorClose}
                    variant="filled"
                    severity="info"
                    sx={{ width: '100%', backgroundColor: '#202024' }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

const MappedInvoicesTable = ({
    data,
    onArchiveInvoice,
    page,
    pageSize,
    invoicesTotal,
    dealsTotal,
    loadPage,
}: {
    data: FundNowDataProps;
    onArchiveInvoice?: (invoiceId: string) => void;
    page: number;
    pageSize: number;
    invoicesTotal: number;
    dealsTotal: number;
    loadPage: (page: number, pageSize: number) => Promise<void> | void;
}) => {
    const [tabValue, setTabValue] = useState(0);
    const [hasActiveFilters, setHasActiveFilters] = useState(false);
    const gridRef = useRef<AgGridReact>(null);
    const rowData = useMemo(() => {
        return [
            ...(data.invoices
                ?.filter(invoice => invoice.status === InvoiceStatus.Created)
                .map(invoice => ({
                    id: invoice.id,
                    type: RowType.Invoice,
                    customer: invoice.customer_name || NULL_VALUE_FALLBACK,
                    status: invoiceOrDealStatusToLabel[invoice.status] || NULL_VALUE_FALLBACK,
                    invoice_number: invoice.invoice_number || NULL_VALUE_FALLBACK,
                    invoice_date: invoice.date || NULL_VALUE_FALLBACK,
                    amount: invoice.total != null ? invoice.total : NULL_VALUE_FALLBACK,
                    due_date: invoice.due_date || NULL_VALUE_FALLBACK,
                    funding_date: NULL_VALUE_FALLBACK,
                    first_payment_date: NULL_VALUE_FALLBACK,
                    payment_frequency: NULL_VALUE_FALLBACK,
                    number_of_payments: NULL_VALUE_FALLBACK,
                    advance_amount: NULL_VALUE_FALLBACK,
                    total_collected: NULL_VALUE_FALLBACK,
                })) || []),
            ...(data.deals?.map(deal => ({
                id: deal.id,
                type: RowType.Deal,
                customer: deal.customer_name || NULL_VALUE_FALLBACK,
                status:
                    deal.deal_status === DealStatus.Submitted &&
                    deal.status === DealDisplayStatus.Rejected
                        ? InvoiceTableRowStatus.Rejected
                        : invoiceOrDealStatusToLabel[deal.deal_status] || NULL_VALUE_FALLBACK,
                invoice_number: deal.invoice_number || NULL_VALUE_FALLBACK,
                invoice_date: deal.funded_date || NULL_VALUE_FALLBACK,
                amount:
                    deal.gross_funded_amount != null
                        ? deal.gross_funded_amount
                        : NULL_VALUE_FALLBACK,
                due_date: deal.first_payment_date || NULL_VALUE_FALLBACK,
                funding_date: deal.funded_date || NULL_VALUE_FALLBACK,
                first_payment_date: deal.first_payment_date || NULL_VALUE_FALLBACK,
                payment_frequency: deal.payment_frequency || NULL_VALUE_FALLBACK,
                number_of_payments: deal.number_of_payments || NULL_VALUE_FALLBACK,
                advance_amount: deal.advance_amount || NULL_VALUE_FALLBACK,
                total_collected: deal.total_collected || NULL_VALUE_FALLBACK,
            })) || []),
        ];
    }, [data]);

    const filteredRowData = useMemo(() => {
        return rowData.filter(row => {
            if (tabValue === 0) return true;
            if (tabValue === 1) return row.status === InvoiceTableRowStatus.Ready;
            if (tabValue === 2)
                return (
                    row.status !== InvoiceTableRowStatus.Ready &&
                    row.status !== InvoiceTableRowStatus.Rejected
                );
        });
    }, [rowData, tabValue]);

    const checkForActiveFilters = useCallback(() => {
        if (gridRef.current?.api) {
            const filterModel = gridRef.current.api.getFilterModel();
            const hasFilters = Object.keys(filterModel || {}).length > 0;
            setHasActiveFilters(hasFilters);
        }
    }, []);

    const handleFilterChanged = useCallback(() => {
        checkForActiveFilters();
    }, [checkForActiveFilters]);

    const handleResetFilters = useCallback(() => {
        gridRef.current!.api.setFilterModel(null);
        setHasActiveFilters(false);
    }, []);

    // Custom export with specific columns
    const onBtnExportCustom = useCallback(() => {
        gridRef.current!.api.exportDataAsCsv({
            fileName: `fundnow-invoices-${new Date().toISOString().split('T')[0]}.csv`,
            columnKeys: [
                'customer',
                'invoice_number',
                'status',
                'amount',
                'invoice_date',
                'due_date',
                'funding_date',
                'advance_amount',
                'first_payment_date',
                'payment_frequency',
                'number_of_payments',
                'total_collected',
            ],
            skipColumnHeaders: false,
            skipColumnGroupHeaders: true,
            processCellCallback: params => {
                // Custom formatting for specific cells
                if (params.column.getColId() === 'amount' && typeof params.value === 'number') {
                    return `$${params.value.toFixed(2)}`;
                }
                return params.value;
            },
        });
    }, []);

    const currentPage = page || 1;
    const total = (invoicesTotal || 0) + (dealsTotal || 0);
    const pageCount = total && pageSize ? Math.ceil(total / pageSize) : 0;

    const currentDataLength = (filteredRowData as any[])?.length || 0;
    const startIndex = currentDataLength > 0 ? 1 : 0;
    const endIndex = currentDataLength;

    const handlePrevPage = useCallback(async () => {
        if (currentPage <= 1) return;
        await loadPage(currentPage - 1, pageSize);
    }, [currentPage, pageSize, loadPage]);

    const handleNextPage = useCallback(async () => {
        if (pageCount && currentPage >= pageCount) return;
        await loadPage(currentPage + 1, pageSize);
    }, [currentPage, pageSize, pageCount, loadPage]);

    const handlePageSizeChange = useCallback(
        async (event: any) => {
            const newSize = Number(event.target.value);
            if (!newSize || newSize === pageSize) return;
            await loadPage(1, newSize);
        },
        [pageSize, loadPage]
    );

    return (
        <Box
            sx={{
                width: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                border: '1px solid #ddd',
                borderRadius: '8px',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #ddd',
                }}
            >
                <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)}>
                    <Tab label="All" value={0} />
                    <Tab label="Unscheduled" value={1} />
                    <Tab label="Scheduled" value={2} />
                </Tabs>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 0,
                        alignItems: 'center',
                        px: 1,
                    }}
                >
                    <Tooltip title="Reset all filters" placement="top">
                        <IconButton
                            size="small"
                            color={'info'}
                            onClick={handleResetFilters}
                            sx={{
                                'borderRadius': '4px',
                                'backgroundColor': hasActiveFilters ? 'info.main' : 'transparent',
                                'color': hasActiveFilters ? 'info.contrastText' : 'inherit',
                                '&:hover': {
                                    backgroundColor: hasActiveFilters
                                        ? 'info.dark'
                                        : 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <FilterListOffIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export CSV" placement="top">
                        <IconButton
                            size="small"
                            color="info"
                            onClick={onBtnExportCustom}
                            sx={{
                                borderRadius: '4px',
                            }}
                        >
                            <DownloadIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Suspense fallback={<SuspenseFallback key={data === null ? 'loading' : 'loaded'} />}>
                <InvoicesTable
                    rowData={filteredRowData}
                    gridRef={gridRef}
                    onFilterChanged={handleFilterChanged}
                    useGridPagination={false}
                    actionCallbacks={{
                        onArchive: onArchiveInvoice,
                    }}
                />
            </Suspense>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    px: 1.5,
                    borderTop: '1px solid #ddd',
                }}
            >
                <Typography variant="body2">Page Size:</Typography>
                <Select
                    size="small"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    color="info"
                    sx={{
                        'minWidth': 72,
                        '& .MuiSelect-select': {
                            padding: '6px 12px',
                        },
                    }}
                >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                </Select>
                <Typography variant="body2" sx={{ mx: 1 }}>
                    {startIndex} to {endIndex} of {currentDataLength}
                </Typography>
                <Typography variant="body2" sx={{ mx: 1 }}>
                    Page {pageCount ? currentPage : 0} of {pageCount || 0}
                </Typography>
                <IconButton size="small" onClick={handlePrevPage} disabled={currentPage <= 1}>
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={handleNextPage}
                    disabled={pageCount ? currentPage >= pageCount : true}
                >
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
};

const SuspenseFallback = () => {
    return (
        <Box sx={{ width: '100%', height: '100%', flex: 1 }}>
            <Skeleton variant="rectangular" height={100} />
        </Box>
    );
};
